/**
 * When API-stored logs omit `links`, infer deep links from `details` (comma-separated
 * segments produced by engine health checks).
 */

import Utils from './Utils';

function dedupeLinks(links) {
    const seen = new Set();
    return links.filter((l) => {
        const k = `${l.resource}:${l.id}`;
        if (seen.has(k))
            return false;
        seen.add(k);
        return true;
    });
}

const INTEGRATION_TEST_PREFIXES = [
    ['integrations.eventival', 'eventival'],
    ['integrations.fiona', 'fiona'],
    ['integrations.mailchimp', 'mailchimp'],
    ['integrations.brevo', 'brevo'],
    ['integrations.getresponse', 'getresponse'],
    ['integrations.festivalscope', 'shift72'],
    ['integrations.own_it', 'own_it'],
    ['integrations.json', 'json'],
    ['integrations.wordpress', 'wp'],
];

export const integration_key_from_health_test = (test) => {
    if (!test || typeof test !== 'string')
        return null;

    for (const [prefix, key] of INTEGRATION_TEST_PREFIXES) {
        if (test === prefix || test.startsWith(`${prefix}.`))
            return key;
    }

    return null;
};

export const settings_integrations_health_link = (integration_key, setting_id = 'default') => ({
    resource: 'settings_integrations',
    id: integration_key ? `${setting_id}/${integration_key}` : setting_id,
});

/**
 * Replace generic integration catalog links with integration-specific ones when known.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string }>} links
 */
export const resolve_integration_health_check_links = (test, links) => {
    const integration_key = integration_key_from_health_test(test);
    if (!integration_key)
        return dedupeLinks(links || []);

    // Fixes live in WordPress admin (engine already sends wordpress_admin links).
    if (
        test === 'integrations.wordpress.templates.outdated'
        || test === 'integrations.wordpress.pos.edition'
        || test.startsWith('integrations.wordpress.pages.')
    )
        return dedupeLinks(links || []);

    const specific = settings_integrations_health_link(integration_key);
    const filtered = (links || []).filter((link) => !(
        link.resource === 'settings_integrations' && link.id === 'default'
    ));

    return dedupeLinks([specific, ...filtered]);
};

/**
 * Ticket-type / screening form tabs to open for a given health-check test.
 * Matches deeplink paths used by layout editors (e.g. `/tickettypes/edit/:id/emails`).
 */
const HEALTH_CHECK_LINK_TABS_BY_TEST = {
    email_layouts_exist: { tickettype: 'emails' },
    pdf_layouts_exist: { tickettype: 'pdflayouts' },
    eventival_refs: { tickettype: 'pricings' },
    tickettypes_have_window: { tickettype: 'windows' },
    windows_screenings_exist: { tickettype: 'windows' },
    fields_salepoints_exist: { tickettype: 'fields' },
    buckets_tickettypes_have_window: { tickettype: 'windows', screening: 'buckets' },
    buckets_tickettypes_exist: { screening: 'buckets' },
    buckets_steals: { screening: 'buckets' },
    buckets_entry_check_and_one_time_pass: { screening: 'buckets' },
};

/**
 * Attach form-tab deep links where the failing check points at a known editor tab.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string, tab?: string }>} links
 * @returns {Array<{ resource: string, id: string, tab?: string }>}
 */
export const enrich_health_check_links_with_tabs = (test, links) => {
    const tabs = HEALTH_CHECK_LINK_TABS_BY_TEST[test];
    if (!tabs)
        return links || [];

    return (links || []).map((link) => {
        const tab = tabs[link.resource];
        if (!tab || link.tab)
            return link;
        return { ...link, tab };
    });
};

/**
 * When a configured default layout UUID is missing, open Settings → Default layouts
 * rather than the orphaned layout editor URL.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string, tab?: string }>} links
 */
export const enrich_settings_default_layout_health_check_links = (test, links) => {
    if (!test || typeof test !== 'string' || !test.includes('layout_exists') || test.includes('has_fields'))
        return links || [];

    const filtered = (links || []).filter((link) => (
        link.resource !== 'email_layout' && link.resource !== 'pdf_layout'
    ));

    return dedupeLinks([
        { resource: 'settings_default_layouts', id: 'default' },
        ...filtered,
    ]);
};

const MAX_SCREENING_LINKS = 8;

/**
 * For buckets_tickettypes_exist, ticket types are missing from the current edition
 * (often present in another edition). Linking to them is misleading — keep screenings only.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string, tab?: string }>} links
 */
export const prune_buckets_tickettypes_exist_links = (test, links) => {
    if (test !== 'buckets_tickettypes_exist')
        return links || [];

    const screenings = (links || []).filter((link) => link.resource === 'screening');
    const capped = screenings.slice(0, MAX_SCREENING_LINKS);
    const overflow = screenings.length > MAX_SCREENING_LINKS
        ? [{ resource: 'screenings_index', id: 'list' }]
        : [];

    return dedupeLinks([...capped, ...overflow]);
};

/**
 * For bucketlists tickettypes_exist, the ticket types are gone — fix the quota configs.
 * Linking to missing ticket types is misleading; keep bucketlist links only.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string, tab?: string }>} links
 */
export const prune_tickettypes_exist_links = (test, links) => {
    if (test !== 'tickettypes_exist')
        return links || [];

    return dedupeLinks((links || []).filter((link) => link.resource === 'bucketlist'));
};

/**
 * Resolve integration-specific links, then attach known form tabs.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string, tab?: string }>} links
 */
export const resolve_health_check_links = (test, links) => (
    enrich_health_check_links_with_tabs(
        test,
        prune_tickettypes_exist_links(
            test,
            prune_buckets_tickettypes_exist_links(
                test,
                enrich_settings_default_layout_health_check_links(
                    test,
                    resolve_integration_health_check_links(test, links)
                )
            )
        )
    )
);

const TICKETTYPE_ID = /^[a-zA-Z0-9_-]+$/;
const RESOURCE_ID = /^[a-zA-Z0-9_-]+$/;
const UUID_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EVENTIVAL_FORM_DETAIL = /^(\S+)\s*>\s*(pricinglist|bucketlist)\s*>\s*(\S+)$/;

/**
 * First token of each comma-separated engine details chunk (`id` or `id > …`).
 *
 * @param {string} details
 * @returns {string[]}
 */
const ids_from_health_check_details = (details) => {
    const ids = [];
    String(details).split(/\s*,\s*/).forEach((chunk) => {
        const line = chunk.trim();
        const m = line.match(/^([a-zA-Z0-9_-]+)/);
        if (m && RESOURCE_ID.test(m[1]))
            ids.push(m[1]);
    });
    return ids;
};

/**
 * @param {string} groupKey
 * @param {string} test
 * @returns {Array<{ resource: string, id: string }>}
 */
export function inferHealthCheckLinksFromTest(groupKey, test) {
    if (groupKey === 'screenings' && test === 'festivalscope_refs')
        return [settings_integrations_health_link('shift72')];
    if (groupKey === 'screenings' && test === 'own_it_refs')
        return [settings_integrations_health_link('own_it')];
    if (groupKey === 'equipment_orders' && test === 'not_submitted')
        return [{ resource: 'equipment_orders', id: 'list' }];
    if (groupKey === 'activities' && test === 'reward_references_exist')
        return [];
    if (groupKey === 'screenings' && test === 'eventival_custom_forms')
        return [settings_integrations_health_link('eventival')];
    if (groupKey === 'settings' && (test === 'images.logo' || test === 'images.sizes'))
        return [{ resource: 'settings_images', id: 'default' }];
    if (groupKey === 'settings' && test === 'mobile.images')
        return [{ resource: 'settings_mobile', id: 'default' }];
    if (
        groupKey === 'settings'
        && (test === 'customer.email.contact_configured' || test === 'customer.email.not_seed_placeholder')
    )
        return [{ resource: 'settings_customer', id: 'default' }];

    const registration_mode_match = typeof test === 'string'
        ? test.match(/^eshop\.(?:newsletter|survey)_registration\.modes\.(mailchimp|brevo|getresponse)$/)
        : null;
    if (groupKey === 'settings' && registration_mode_match) {
        return [
            { resource: 'settings_eshop', id: 'default' },
            settings_integrations_health_link(registration_mode_match[1]),
        ];
    }

    // Needs the WP site URI from the engine payload; do not fall back to Kronos settings.
    if (
        test === 'integrations.wordpress.templates.outdated'
        || test === 'integrations.wordpress.pos.edition'
        || (typeof test === 'string' && test.startsWith('integrations.wordpress.pages.'))
    )
        return [];

    const integration_key = integration_key_from_health_test(test);
    if (integration_key)
        return [settings_integrations_health_link(integration_key)];

    return [];
}

/**
 * @param {string} groupKey
 * @param {string} test
 * @param {string} details
 * @returns {Array<{ resource: string, id: string }>}
 */
export function inferHealthCheckLinksFromDetails(groupKey, test, details) {
    if (!details || typeof details !== 'string')
        return [];

    const trimmed = details.trim();
    if (!trimmed)
        return [];

    const out = [];

    if (groupKey === 'activities' && test === 'reward_references_exist') {
        trimmed.split(/\s*,\s*/).forEach((chunk) => {
            const line = chunk.trim();
            const m = line.match(/^([0-9a-f-]{36})\s*>/i);
            if (m)
                out.push({ resource: 'activity', id: m[1] });
        });
        return dedupeLinks(out);
    }

    if (groupKey === 'tickettypes' && test === 'referenced_by_buckets') {
        trimmed.split(/\s*,\s*/).forEach((id) => {
            const x = id.trim();
            if (TICKETTYPE_ID.test(x))
                out.push({ resource: 'tickettype', id: x });
        });
        return dedupeLinks(out);
    }

    if (groupKey === 'tickettypes' && test === 'eventival_refs') {
        trimmed.split(/\s*,\s*/).forEach((chunk) => {
            const line = chunk.trim();
            const m = line.match(/^([a-zA-Z0-9_-]+)\s*>/);
            if (m)
                out.push({ resource: 'tickettype', id: m[1] });
        });
        return dedupeLinks(out);
    }

    if (groupKey === 'tickettypes' && (
        test === 'pdf_layouts_exist'
        || test === 'email_layouts_exist'
        || test === 'windows_screenings_exist'
        || test === 'fields_salepoints_exist'
    )) {
        trimmed.split(/\s*,\s*/).forEach((chunk) => {
            const line = chunk.trim();
            const m = line.match(/^([a-zA-Z0-9_-]+)\s*>/);
            if (m)
                out.push({ resource: 'tickettype', id: m[1] });
        });
        return dedupeLinks(out);
    }

    if (groupKey === 'salepoints' && (
        test === 'places_exist'
        || test === 'delivery_types_exist'
    )) {
        trimmed.split(/\s*,\s*/).forEach((chunk) => {
            const line = chunk.trim();
            const m = line.match(/^([0-9a-f-]{36})\s*>/i);
            if (m)
                out.push({ resource: 'salepoint', id: m[1] });
        });
        return dedupeLinks(out);
    }

    if (test === 'stale_years' || test === 'invalid_hrefs') {
        const resource = groupKey === 'tickettypes'
            ? 'tickettype'
            : groupKey === 'email_layouts'
                ? 'email_layout'
                : groupKey === 'pdf_layouts'
                    ? 'pdf_layout'
                    : null;
        if (resource) {
            ids_from_health_check_details(trimmed).forEach((id) => {
                out.push({ resource, id });
            });
            return dedupeLinks(out);
        }
    }

    if (
        groupKey === 'cashregisters'
        && (test === 'closed_since_last_year' || test === 'cash_float')
    ) {
        ids_from_health_check_details(trimmed).forEach((id) => {
            if (UUID_ID.test(id))
                out.push({ resource: 'cashregister', id });
        });
        return dedupeLinks(out);
    }

    if (groupKey === 'screenings' && test === 'eventival_custom_forms') {
        trimmed.split(/\s*,\s*/).forEach((chunk) => {
            const m = chunk.trim().match(EVENTIVAL_FORM_DETAIL);
            if (m)
                out.push({ resource: m[2], id: m[3] });
        });
        return dedupeLinks(out);
    }

    if (groupKey === 'tasks' && test === 'retention.active') {
        return dedupeLinks([
            { resource: 'settings_retention', id: 'default' },
        ]);
    }

    return [];
}

const LEGACY_RETENTION = {
    'missing retention task': 'retention_task_missing',
    'retention task disabled': 'retention_task_disabled',
    'retention settings disabled': 'retention_settings_disabled',
};

const SCREENING_BUCKET_TYPE_LINE = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s+(\S+)\s*>\s*(\S+)$/i;
const SUMMARY_TYPES_LINE = /^(\d+)\s+screening(?:\(s\)|s)?\s*[·|]\s*missing tickettypes?:\s*(.+)$/i;
const SUMMARY_BUCKETLIST_TYPES_LINE = /^(\d+)\s+bucketlist(?:\(s\)|s)?\s*[·|]\s*missing tickettypes?:\s*(.+)$/i;
/** Legacy engine dump: `Config name reserved_admin > missing_tickettype_id`. */
const BUCKETLIST_TYPE_LINE = /^(.+)\s+(\S+)\s*>\s*(\S+)$/;

/**
 * Summarize engine details for buckets_tickettypes_exist (legacy UUID dump or compact form).
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 */
export function format_buckets_tickettypes_exist_details(t, details) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    const summary = trimmed.match(SUMMARY_TYPES_LINE);
    if (summary) {
        return t('health.details.buckets_tickettypes_exist', {
            screenings: summary[1],
            types: summary[2].trim(),
        });
    }

    const screenings = new Set();
    const types = new Set();
    let matched = 0;

    trimmed.split(/\s*,\s*/).forEach((chunk) => {
        const m = chunk.trim().match(SCREENING_BUCKET_TYPE_LINE);
        if (!m)
            return;
        matched += 1;
        screenings.add(m[1]);
        types.add(m[3]);
    });

    if (!matched)
        return details;

    return t('health.details.buckets_tickettypes_exist', {
        screenings: screenings.size,
        types: [...types].join(', '),
    });
}

/**
 * Summarize engine details for bucketlists tickettypes_exist (legacy dump or compact form).
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 */
export function format_bucketlists_tickettypes_exist_details(t, details) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    const summary = trimmed.match(SUMMARY_BUCKETLIST_TYPES_LINE);
    if (summary) {
        return t('health.details.tickettypes_exist', {
            configs: summary[1],
            types: summary[2].trim(),
        });
    }

    const configs = new Set();
    const types = new Set();
    let matched = 0;

    trimmed.split(/\s*,\s*/).forEach((chunk) => {
        const m = chunk.trim().match(BUCKETLIST_TYPE_LINE);
        if (!m)
            return;
        matched += 1;
        configs.add(m[1]);
        types.add(m[3]);
    });

    if (!matched)
        return details;

    return t('health.details.tickettypes_exist', {
        configs: configs.size,
        types: [...types].join(', '),
    });
}

const DETAILS_SEPARATOR = ' · ';
const SEGMENT_SEPARATOR = ' › ';

const PRINTER_LABEL_KEYS = {
    paper: 'layouts.target_paper',
    thermal: 'layouts.target_thermal',
    card: 'layouts.target_card',
};

/** Engine appends the actual kind of an unexpected layout, e.g. ` (pdf/ticket)`. */
const LAYOUT_KIND_SUFFIX = /\s*\(([^()/]+)\/([^()/]+)\)\s*$/;

const short_id = (id) => {
    const value = String(id ?? '');
    return value.length > 8 ? `${value.slice(0, 8)}…` : value;
};

const find_tickettype = (context, id) => (
    (context?.tickettypes || []).find((tickettype) => String(tickettype._id) === String(id))
);

const find_activity = (context, id) => (
    (context?.activities || []).find((activity) => (
        String(activity.id || activity._id) === String(id)
    ))
);

/**
 * Localized ticket-type name, falling back to the raw engine id.
 *
 * @param {string} id
 * @param {{ tickettypes?: Array<Object>, lang?: string }} [context]
 */
export const health_tickettype_label = (id, context) => {
    const tickettype = find_tickettype(context, id);
    if (!tickettype)
        return id;

    return Utils.localized_or_fallback(tickettype.name, context?.lang, id) || id;
};

/**
 * Localized activity (fonction) name, falling back to a short id.
 *
 * @param {string} id
 * @param {{ activities?: Array<Object>, lang?: string }} [context]
 */
export const health_activity_label = (id, context) => {
    const activity = find_activity(context, id);
    if (!activity)
        return short_id(id);

    return Utils.localized_or_fallback(activity.name, context?.lang, short_id(id)) || short_id(id);
};

const pricing_label = (tickettype_id, pricing, context) => {
    const tickettype = find_tickettype(context, tickettype_id);
    const name = tickettype?.pricings?.[pricing]?.name;
    if (!name)
        return pricing;

    return Utils.localized_or_fallback(name, context?.lang, pricing) || pricing;
};

const layout_label = (t, id, context, kind) => {
    const layout = (context?.layouts || []).find((l) => String(l._id) === String(id));
    if (!layout)
        return t('health.details.layout_missing', { id: short_id(id) });

    const name = Utils.localized_or_fallback(layout.name, context?.lang, id) || id;
    if (!kind)
        return name;

    return t('health.details.layout_wrong_type', {
        name,
        layout_type: kind[0],
        type: kind[1],
    });
};

/**
 * Localized layout name for health-check deep links.
 *
 * @param {string} id
 * @param {{ layouts?: Array<Object>, lang?: string }} [context]
 */
export const health_layout_label = (id, context) => {
    const layout = (context?.layouts || []).find((l) => String(l._id) === String(id));
    if (!layout)
        return short_id(id);

    return Utils.localized_or_fallback(layout.name, context?.lang, short_id(id)) || short_id(id);
};

/**
 * Localized bucketlist / contingent config name.
 *
 * @param {string} id
 * @param {{ bucketlists?: Array<Object>, lang?: string }} [context]
 */
export const health_bucketlist_label = (id, context) => {
    const bucketlist = (context?.bucketlists || []).find((b) => String(b._id) === String(id));
    if (!bucketlist)
        return short_id(id);

    return Utils.localized_or_fallback(bucketlist.name, context?.lang, short_id(id)) || short_id(id);
};

/** Locale segment at the end of engine paths (`widgets.body.fr`, `name.en`). */
const PATH_LANG_RE = /^[a-z]{2}(?:-[a-z]{2})?$/i;

/**
 * Parse one engine path segment into optional widget id + content language.
 *
 * @param {string} path
 * @returns {{ widget: string|null, lang: string|null }}
 */
export function parse_health_check_layout_path(path) {
    const parts = String(path || '').split('.').filter(Boolean);
    if (!parts.length)
        return { widget: null, lang: null };

    let lang = null;
    if (PATH_LANG_RE.test(parts[parts.length - 1])) {
        lang = parts[parts.length - 1].toLowerCase();
        parts.pop();
    }

    let widget = null;
    if (parts[0] === 'widgets' && parts.length >= 2)
        widget = parts.slice(1).join('.');

    return { widget, lang };
}

/**
 * Parse engine `id > path > issue` detail chunks.
 *
 * @param {string} details
 * @returns {Array<{ id: string, path: string, widget: string|null, lang: string|null, issue: string }>}
 */
export function parse_path_detail_chunks(details) {
    const out = [];
    String(details || '').split(/\s*,\s*/).forEach((chunk) => {
        const parts = chunk.trim().split(/\s*>\s*/).filter(Boolean);
        if (parts.length < 2)
            return;
        const id = parts[0];
        const issue = parts[parts.length - 1];
        const path = parts.length >= 3 ? parts.slice(1, -1).join(' > ') : '';
        const { widget, lang } = parse_health_check_layout_path(path);
        out.push({ id, path, widget, lang, issue });
    });
    return out;
}

/**
 * Group engine `id > path > issue` detail chunks by entity id.
 *
 * @param {string} details
 * @returns {Map<string, string[]>}
 */
export function group_path_detail_issues(details) {
    const by_id = new Map();
    parse_path_detail_chunks(details).forEach(({ id, issue }) => {
        if (!by_id.has(id))
            by_id.set(id, []);
        const list = by_id.get(id);
        if (!list.includes(issue))
            list.push(issue);
    });
    return by_id;
}

const LINK_ISSUE_ACTION_BY_TEST = {
    invalid_hrefs: 'fix_links',
    stale_years: 'fix_years',
};

const LAYOUT_LINK_RESOURCES = new Set(['email_layout', 'pdf_layout']);

/**
 * Expand layout links into one deep link per (layout, widget, lang) hit.
 *
 * @param {Array<{ resource: string, id: string }>} links
 * @param {string} details
 * @param {string} action
 * @returns {Array<Object>}
 */
function enrich_layout_links_with_path_issues(links, details, action) {
    const chunks = parse_path_detail_chunks(details);
    if (!chunks.length)
        return links;

    const by_layout = new Map();
    chunks.forEach((chunk) => {
        if (!by_layout.has(chunk.id))
            by_layout.set(chunk.id, []);
        by_layout.get(chunk.id).push(chunk);
    });

    const out = [];
    links.forEach((link) => {
        if (!LAYOUT_LINK_RESOURCES.has(link.resource)) {
            out.push(link);
            return;
        }

        const layout_chunks = by_layout.get(String(link.id));
        if (!layout_chunks?.length) {
            out.push(link);
            return;
        }

        const groups = new Map();
        layout_chunks.forEach(({ widget, lang, issue }) => {
            const key = `${widget || ''}\0${lang || ''}`;
            if (!groups.has(key))
                groups.set(key, { widget: widget || undefined, lang: lang || undefined, issues: [] });
            const group = groups.get(key);
            if (!group.issues.includes(issue))
                group.issues.push(issue);
        });

        groups.forEach((group) => {
            out.push({
                ...link,
                ...(group.widget ? { widget: group.widget } : {}),
                ...(group.lang ? { lang: group.lang } : {}),
                issues: group.issues,
                issue_action: action,
            });
        });
    });
    return out;
}

/**
 * Attach human-facing issue lists to deep links for path-dump health checks.
 *
 * @param {string} test
 * @param {Array<{ resource: string, id: string }>} links
 * @param {string} details
 * @returns {Array<Object>}
 */
export function enrich_health_check_links_with_issues(test, links, details) {
    const action = LINK_ISSUE_ACTION_BY_TEST[test];
    if (!action || !Array.isArray(links) || !links.length)
        return links || [];

    const has_layout_link = links.some((link) => LAYOUT_LINK_RESOURCES.has(link.resource));
    if (has_layout_link)
        return enrich_layout_links_with_path_issues(links, details, action);

    const by_id = group_path_detail_issues(details);
    if (!by_id.size)
        return links;

    return links.map((link) => {
        const issues = by_id.get(String(link.id));
        if (!issues?.length)
            return link;
        return { ...link, issues, issue_action: action };
    });
}

/**
 * Hide the raw engine path dump when deep links already carry the issues.
 *
 * @param {string} test
 * @param {Array<Object>} links
 * @returns {boolean}
 */
export function should_hide_raw_health_check_details(test, links) {
    if (!LINK_ISSUE_ACTION_BY_TEST[test])
        return false;
    return Array.isArray(links) && links.some((link) => link.issues?.length);
}

/**
 * Turn `tickettype > salepoint_id` engine chunks into readable sentences.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 * @param {{ tickettypes?: Array<Object>, lang?: string }} [context]
 */
export function format_fields_salepoints_exist_details(t, details, context) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    let matched = 0;
    const lines = trimmed.split(/\s*,\s*/).map((chunk) => {
        const m = chunk.trim().match(/^([a-zA-Z0-9_-]+)\s*>\s*(\S+)$/);
        if (!m)
            return chunk.trim();
        matched += 1;
        return [
            health_tickettype_label(m[1], context),
            t('health.details.salepoint_missing', { id: short_id(m[2]) }),
        ].join(SEGMENT_SEPARATOR);
    });

    if (!matched)
        return details;

    return lines.join(DETAILS_SEPARATOR);
}

/**
 * Turn `tickettype > screening_id` engine chunks into readable sentences.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 * @param {{ tickettypes?: Array<Object>, lang?: string }} [context]
 */
export function format_windows_screenings_exist_details(t, details, context) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    let matched = 0;
    const lines = trimmed.split(/\s*,\s*/).map((chunk) => {
        const m = chunk.trim().match(/^([a-zA-Z0-9_-]+)\s*>\s*(\S+)$/);
        if (!m)
            return chunk.trim();
        matched += 1;
        return [
            health_tickettype_label(m[1], context),
            t('health.details.screening_missing', { id: short_id(m[2]) }),
        ].join(SEGMENT_SEPARATOR);
    });

    if (!matched)
        return details;

    return lines.join(DETAILS_SEPARATOR);
}

/**
 * Turn `tickettype > [pricing >] printer|hook > layout_id` engine chunks into readable
 * sentences using the loaded ticket types and layouts.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 * @param {{ tickettypes?: Array<Object>, layouts?: Array<Object>, lang?: string }} [context]
 * @param {'pdf'|'email'} target
 */
export function format_tickettype_layouts_details(t, details, context, target) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    let matched = 0;
    const lines = trimmed.split(/\s*,\s*/).map((chunk) => {
        const raw = chunk.trim();
        const kind = raw.match(LAYOUT_KIND_SUFFIX);
        const segments = (kind ? raw.slice(0, kind.index) : raw)
            .split('>')
            .map((s) => s.trim())
            .filter(Boolean);
        if (segments.length < 3)
            return raw;

        matched += 1;
        const tickettype_id = segments[0];
        const layout_id = segments[segments.length - 1];
        const middle = segments.slice(1, -1).map((segment, index, all) => {
            if (index < all.length - 1)
                return pricing_label(tickettype_id, segment, context);
            if (target === 'email')
                return t(`layouts.hook_${segment}`, { defaultValue: segment });
            return PRINTER_LABEL_KEYS[segment] ? t(PRINTER_LABEL_KEYS[segment]) : segment;
        });

        return [
            health_tickettype_label(tickettype_id, context),
            ...middle,
            layout_label(t, layout_id, context, kind ? [kind[1], kind[2]] : null),
        ].join(SEGMENT_SEPARATOR);
    });

    if (!matched)
        return details;

    return lines.join(DETAILS_SEPARATOR);
}

const WP_SITE_PREFIX = /^(.+?)\s*->\s*(.+)$/;
const WP_POS_STALE = /^stored POS not available for active edition (.+?) — re-save Ticketack API settings in WordPress$/i;
const WP_POS_SALEPOINT_EDITION = /^salepoint edition (.+?) \(active (.+)\)$/i;
const WP_POS_CASHREGISTER_EDITION = /^cashregister edition (.+?) \(active (.+)\)$/i;
const WP_AUTH_MISSING = /^missing:\s*(.+)$/i;
const WP_PAGES_MISSING = /^missing pages:\s*(.+)$/i;
const WP_PAGE_MISSING_SLUG = /^page missing \((.+)\)$/i;
const WP_THANK_YOU_SHORTCODE = /^missing shortcode tkt_thank_you$/i;
const WP_SHORTCODE_LINE = /^([a-z0-9_]+)(?:\s*\(([^)]+)\))?:\s*missing\s+(.+)$/i;
const WP_PAGE_ENTRY = /^([a-z0-9_]+)\s*\((.+)\)$/i;

/**
 * Localized label for a Ticketack WP page config key.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} key
 */
function wordpress_page_label(t, key) {
    return t(`health.wordpress_page.${key}`, { defaultValue: key });
}

/**
 * Translate a single WordPress health-check detail fragment.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} testSuffix e.g. cached_config, pages.auth
 * @param {string} fragment
 */
function format_wordpress_detail_fragment(t, testSuffix, fragment) {
    const part = String(fragment || '').trim();
    if (!part)
        return part;

    if (testSuffix === 'cached_config') {
        if (part === 'missing')
            return t('health.wordpress_error.config_missing');
        if (part === 'outdated')
            return t('health.wordpress_error.config_outdated');
    }

    if (testSuffix === 'version' && part === 'Could not get last version')
        return t('health.wordpress_error.version_unavailable');

    if (testSuffix === 'pos.edition') {
        const stale = part.match(WP_POS_STALE);
        if (stale)
            return t('health.wordpress_error.pos_stale', { edition: stale[1] });
        if (part === 'salepoint missing')
            return t('health.wordpress_error.salepoint_missing');
        const sp = part.match(WP_POS_SALEPOINT_EDITION);
        if (sp)
            return t('health.wordpress_error.salepoint_edition', { edition: sp[1], active: sp[2] });
        if (part === 'cashregister missing')
            return t('health.wordpress_error.cashregister_missing');
        const cr = part.match(WP_POS_CASHREGISTER_EDITION);
        if (cr)
            return t('health.wordpress_error.cashregister_edition', { edition: cr[1], active: cr[2] });
        if (part === 'cashregister not linked to configured salepoint')
            return t('health.wordpress_error.cashregister_not_linked');
    }

    if (testSuffix === 'pages.auth') {
        const m = part.match(WP_AUTH_MISSING);
        if (m) {
            const pages = m[1].split(',').map((s) => s.trim()).filter(Boolean)
                .map((k) => wordpress_page_label(t, k))
                .join(', ');
            return t('health.wordpress_error.pages_missing', { pages });
        }
    }

    if (testSuffix === 'pages.exist') {
        const m = part.match(WP_PAGES_MISSING);
        if (m) {
            const pages = m[1].split(',').map((s) => s.trim()).filter(Boolean)
                .map((entry) => {
                    const em = entry.match(WP_PAGE_ENTRY);
                    if (!em)
                        return entry;
                    return `${wordpress_page_label(t, em[1])} (${em[2]})`;
                })
                .join(', ');
            return t('health.wordpress_error.pages_missing_pages', { pages });
        }
    }

    if (testSuffix === 'pages.thank_you') {
        if (part === 'not configured')
            return t('health.wordpress_error.thank_you_not_configured');
        if (part === 'same as checkout')
            return t('health.wordpress_error.thank_you_same_as_checkout');
        const missing = part.match(WP_PAGE_MISSING_SLUG);
        if (missing)
            return t('health.wordpress_error.thank_you_page_missing', { slug: missing[1] });
        if (WP_THANK_YOU_SHORTCODE.test(part))
            return t('health.wordpress_error.thank_you_missing_shortcode');
        const shortcode = part.match(WP_SHORTCODE_LINE);
        if (shortcode) {
            const page = wordpress_page_label(t, shortcode[1]);
            const lang = (shortcode[2] || '').trim();
            const tags = shortcode[3].replace(/\|/g, ' / ');
            if (lang) {
                return t('health.wordpress_error.shortcode_missing_lang', {
                    page,
                    lang,
                    tags,
                });
            }
            return t('health.wordpress_error.shortcode_missing', {
                page,
                tags,
            });
        }
    }

    if (testSuffix === 'pages.shortcodes') {
        const m = part.match(WP_SHORTCODE_LINE);
        if (m) {
            const page = wordpress_page_label(t, m[1]);
            const lang = (m[2] || '').trim();
            const tags = m[3].replace(/\|/g, ' / ');
            if (lang) {
                return t('health.wordpress_error.shortcode_missing_lang', {
                    page,
                    lang,
                    tags,
                });
            }
            return t('health.wordpress_error.shortcode_missing', {
                page,
                tags,
            });
        }
    }

    return part;
}

/**
 * Humanize WordPress module health-check details (English codes / phrases → locale).
 * Engine may prefix multi-site results with `SiteName -> `.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} test integrations.wordpress.*
 * @param {string} details
 */
export function format_wordpress_health_details(t, test, details) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    const testSuffix = String(test || '').replace(/^integrations\.wordpress\./, '');

    // Collapsed multi-site rows use " | " between "Site -> fragment" segments.
    const siteSegments = trimmed.split(/\s*\|\s*/).map((s) => s.trim()).filter(Boolean);
    if (siteSegments.length > 1 && siteSegments.every((s) => WP_SITE_PREFIX.test(s))) {
        return siteSegments
            .map((segment) => format_wordpress_health_details(t, test, segment))
            .join(DETAILS_SEPARATOR);
    }

    let sitePrefix = '';
    let body = trimmed;
    const siteMatch = trimmed.match(WP_SITE_PREFIX);
    if (siteMatch) {
        sitePrefix = siteMatch[1].trim();
        body = siteMatch[2].trim();
    }

    const separator = testSuffix === 'pos.edition' || testSuffix === 'pages.shortcodes'
        ? ';'
        : null;
    const fragments = separator
        ? body.split(';').map((s) => s.trim()).filter(Boolean)
        : [body];

    const translated = fragments
        .map((fragment) => format_wordpress_detail_fragment(t, testSuffix, fragment))
        .join(DETAILS_SEPARATOR);

    if (sitePrefix)
        return `${sitePrefix} → ${translated}`;

    return translated;
}

/**
 * Humanize legacy / engine details for editions.current_not_ended.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 */
export function format_edition_current_not_ended_details(t, details) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    const ended = trimmed.match(
        /^Configured edition is ended \(end_date=(.+?)\)\.?$/i
    );
    if (ended) {
        let date = ended[1];
        try {
            const parsed = new Date(ended[1]);
            if (!Number.isNaN(parsed.getTime()))
                date = parsed.toLocaleDateString();
        } catch (_err) {
            /* keep ISO */
        }
        return t('health.details.edition_ended', { date });
    }

    const notFound = trimmed.match(/^edition_not_found:(.+)$/i);
    if (notFound)
        return t('health.details.edition_not_found', { id: short_id(notFound[1]) });

    return details;
}

/**
 * @param {import('i18next').TFunction} t
 * @param {string} groupKey
 * @param {string} test
 * @param {string} details
 * @param {{ tickettypes?: Array<Object>, layouts?: Array<Object>, lang?: string }} [context]
 */
/**
 * Turn activity reward reference chunks into readable sentences.
 * Format: `{activity_id} > tickettype:{id}` | `pricing:{type}/{pricing}` | `article:{aid}/{vid}`
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 * @param {{ tickettypes?: Array<Object>, activities?: Array<Object>, lang?: string }} [context]
 */
export function format_activities_reward_references_details(t, details, context) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    let matched = 0;
    const lines = trimmed.split(/\s*,\s*/).map((chunk) => {
        const line = chunk.trim();
        const tickettype = line.match(/^([0-9a-f-]{36})\s*>\s*tickettype:(.+)$/i);
        if (tickettype) {
            matched += 1;
            return t('health.details.activity_tickettype_missing', {
                activity: health_activity_label(tickettype[1], context),
                tickettype: health_tickettype_label(tickettype[2], context),
            });
        }
        const pricing = line.match(/^([0-9a-f-]{36})\s*>\s*pricing:([^/]+)\/(.+)$/i);
        if (pricing) {
            matched += 1;
            return t('health.details.activity_pricing_missing', {
                activity: health_activity_label(pricing[1], context),
                tickettype: health_tickettype_label(pricing[2], context),
                pricing: pricing[3],
            });
        }
        const article = line.match(/^([0-9a-f-]{36})\s*>\s*article:([^/]+)\/(.+)$/i);
        if (article) {
            matched += 1;
            return t('health.details.activity_article_missing', {
                activity: health_activity_label(article[1], context),
                article: short_id(article[2]),
                variant: short_id(article[3]),
            });
        }
        return line;
    });

    if (!matched)
        return details;

    return lines.join(DETAILS_SEPARATOR);
}

/**
 * Turn `salepoint_id > missing_id` engine chunks into readable sentences.
 *
 * @param {import('i18next').TFunction} t
 * @param {string} details
 * @param {'place'|'delivery_type'} kind
 */
export function format_salepoint_ref_missing_details(t, details, kind) {
    const trimmed = String(details || '').trim();
    if (!trimmed)
        return details;

    const detail_key = kind === 'delivery_type'
        ? 'health.details.delivery_type_missing'
        : 'health.details.place_missing';

    let matched = 0;
    const lines = trimmed.split(/\s*,\s*/).map((chunk) => {
        const m = chunk.trim().match(/^(\S+)\s*>\s*(\S+)$/);
        if (!m)
            return chunk.trim();
        matched += 1;
        return [
            short_id(m[1]),
            t(detail_key, { id: short_id(m[2]) }),
        ].join(SEGMENT_SEPARATOR);
    });

    if (!matched)
        return details;

    return lines.join(DETAILS_SEPARATOR);
}

export function formatHealthCheckDetails(t, groupKey, test, details, context) {
    if (!details || typeof details !== 'string')
        return details;

    if (groupKey === 'votes') {
        return details
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((part) => {
                const key = `health.votes_error.${part}`;
                return t(key, { defaultValue: part });
            })
            .join(' · ');
    }

    if (groupKey === 'settings' && test === 'editions.current_not_ended')
        return format_edition_current_not_ended_details(t, details);

    if (groupKey === 'settings' && typeof test === 'string' && test.startsWith('integrations.wordpress.'))
        return format_wordpress_health_details(t, test, details);

    if (groupKey === 'screenings' && test === 'buckets_tickettypes_exist')
        return format_buckets_tickettypes_exist_details(t, details);

    if (groupKey === 'bucketlists' && test === 'tickettypes_exist')
        return format_bucketlists_tickettypes_exist_details(t, details);

    if (groupKey === 'tickettypes' && test === 'pdf_layouts_exist')
        return format_tickettype_layouts_details(t, details, context, 'pdf');

    if (groupKey === 'tickettypes' && test === 'email_layouts_exist')
        return format_tickettype_layouts_details(t, details, context, 'email');

    if (groupKey === 'tickettypes' && test === 'windows_screenings_exist')
        return format_windows_screenings_exist_details(t, details, context);

    if (groupKey === 'tickettypes' && test === 'fields_salepoints_exist')
        return format_fields_salepoints_exist_details(t, details, context);

    if (groupKey === 'salepoints' && test === 'places_exist')
        return format_salepoint_ref_missing_details(t, details, 'place');

    if (groupKey === 'salepoints' && test === 'delivery_types_exist')
        return format_salepoint_ref_missing_details(t, details, 'delivery_type');

    if (groupKey === 'activities' && test === 'reward_references_exist')
        return format_activities_reward_references_details(t, details, context);

    if (groupKey !== 'tasks' || test !== 'retention.active')
        return details;

    return details
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((part) => {
            const code = LEGACY_RETENTION[part] || part;
            const key = `health.retention_error.${code}`;
            return t(key, { defaultValue: part });
        })
        .join(' · ');
}
